import type { Thread } from "@/api/me";
import { useV3 } from "../useV3";
import { lastAt, textOf } from "./useThreads";
import type { Chat } from "./useThreads";

/* The conversation list, from the design's `sq-conv` rows.
 *
 * NO UNREAD DOT. The design draws one and marks the row `sq-conv--unread`.
 * There is no read state on the server -- the legacy flag was client-only --
 * so every row would either always have a dot or never have one, and a dot
 * that is always on is a badge, not a signal.
 */
export function ConvList({
  chat, threads, openId, onOpen,
}: {
  chat: Chat;
  threads: Thread[];
  openId: string | null;
  onOpen: (id: string) => void;
}) {
  const { s, locale } = useV3();

  const when = (th: Thread) => {
    const ts = lastAt(th);
    if (!ts) return "";
    const d = new Date(ts);
    const today = new Date();
    const intl = locale === "ar" ? "ar-EG-u-nu-latn" : "en-GB";
    return d.toDateString() === today.toDateString()
      ? d.toLocaleTimeString(intl, { hour: "2-digit", minute: "2-digit" })
      : d.toLocaleDateString(intl, { day: "numeric", month: "short" });
  };

  return (
    <div className="sq-conv-list" data-component="conversations">
      {threads.map((th) => {
        const msgs = Array.isArray(th.messages) ? th.messages : [];
        const last = msgs[msgs.length - 1];
        const ctx = chat.contextOf(th);
        return (
          <a key={th.id} href={`#${th.id}`} className="sq-conv"
            aria-current={openId === th.id ? "true" : undefined}
            onClick={(e) => { e.preventDefault(); onOpen(th.id); }}>
            <span className="sq-avatar" aria-hidden="true" />
            <div style={{ minWidth: 0 }}>
              <div className="sq-conv__name">{chat.peerName(th)}</div>
              {/* A thread with no resolvable listing is a plain conversation,
                  not a uuid dressed up as a property. */}
              {ctx && (
                <div className="sq-conv__listing">
                  {ctx.photo && <img src={ctx.photo} alt="" loading="lazy" />}
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {ctx.title}
                  </span>
                </div>
              )}
              <div className="sq-conv__last">{last ? textOf(last) : ""}</div>
            </div>
            <div className="sq-conv__meta">
              <time>{when(th)}</time>
              {/* No unread dot; see the header note. */}
            </div>
          </a>
        );
      })}
      {threads.length === 0 && (
        <p className="sq-field__hint" style={{ padding: 20 }}>{s.chat.emptyBody}</p>
      )}
    </div>
  );
}
